import { useTranslation } from '@doccareplus/i18n';
import { nationalPhoneLengthLimit } from '@doccareplus/shared/lib/phone';
import { DateField, Input, PhoneInput, SearchSelect, Textarea } from '@doccareplus/ui';
import { BLOOD_GROUPS, GENDERS } from '../domain/quickRegisterOptions';
import type { QuickPatientDuplicateMatch } from '../infrastructure/scheduling.api';
import type { UseQuickRegisterPatientStep } from './useQuickRegisterPatientStep';

/**
 * Step 1 of the booking stepper — full patient details, submitted on this component's own `formId` so
 * the parent Drawer/Sheet footer can drive it (mirrors the `FORM_ID` pattern used for booking details).
 * In `readOnly` mode (reached via "Back" after the patient was already created) every field is disabled
 * and duplicate/error UI is hidden — this is a summary of what was submitted, not an editable re-entry.
 */
export function QuickRegisterPatientStep({
  formId,
  form,
  onSubmit,
  onSelectExisting,
  readOnly = false,
}: {
  formId: string;
  form: UseQuickRegisterPatientStep;
  onSubmit: () => void;
  onSelectExisting: (patient: QuickPatientDuplicateMatch) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation('appointments');
  const hasStrongMatch = form.duplicateMatches.some((match) => match.matchReasons?.includes('NameAndPhone'));

  return (
    <form
      id={formId}
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!readOnly) onSubmit();
      }}
    >
      {!readOnly && form.error && (
        <div className="rounded-[10px] border border-danger bg-danger-bg p-2.5 text-[12.5px] font-medium text-danger" role="alert">
          {form.error}
        </div>
      )}

      {!readOnly && form.duplicateMatches.length > 0 && (
        <section
          className={`space-y-2 rounded-lg border p-3 ${hasStrongMatch ? 'border-danger bg-danger-bg' : 'border-warning bg-warning-bg'}`}
        >
          <p className={`text-12 font-bold ${hasStrongMatch ? 'text-danger' : 'text-warning'}`}>
            {t(hasStrongMatch ? 'booking.quickRegister.duplicatesStrong' : 'booking.quickRegister.duplicates')}
          </p>
          {form.duplicateMatches.map((match) => (
            <button
              key={match.id}
              type="button"
              onClick={() => onSelectExisting(match)}
              className="block w-full rounded-md bg-surface p-2 text-left text-12"
            >
              <strong>{match.fullName}</strong>
              <span className="ml-2 text-text-2">{match.patientNumber} · {match.phone}</span>
            </button>
          ))}
          <Textarea
            label={t('booking.quickRegister.reasonLabel')}
            optional
            value={form.duplicateReason}
            onChange={(event) => form.setDuplicateReason(event.target.value)}
            rows={2}
          />
        </section>
      )}

      <Input
        label={t('booking.quickRegister.nameLabel', { defaultValue: 'Full name' })}
        requiredMark
        autoFocus={!readOnly}
        disabled={readOnly}
        placeholder={t('booking.quickRegister.namePlaceholder', { defaultValue: 'e.g. Priya Patel' })}
        value={form.fields.fullName}
        onChange={(event) => form.setField('fullName', event.target.value)}
      />

      <PhoneInput
        label={t('booking.quickRegister.phoneLabel')}
        requiredMark
        disabled={readOnly}
        country={form.fields.phoneCountry}
        onCountryChange={(country) => form.setField('phoneCountry', country)}
        value={form.fields.phone}
        onChange={(value) => form.setField('phone', value)}
        placeholder={t('booking.quickRegister.phonePlaceholder')}
        countryButtonLabel={t('booking.quickRegister.countryButtonLabel')}
        searchPlaceholder={t('common:actions.search')}
        noResultsLabel={t('common:list.noResults')}
        maxLength={nationalPhoneLengthLimit(form.fields.phoneCountry)}
      />

      <div className="grid grid-cols-2 gap-3">
        <DateField
          label={t('booking.quickRegister.dobLabel', { defaultValue: 'Date of birth' })}
          optional
          disabled={readOnly}
          value={form.fields.dateOfBirth || null}
          onChange={(value) => form.setField('dateOfBirth', value)}
          placeholder={t('common:datePicker.chooseDate')}
          prevMonthLabel={t('common:datePicker.previousMonth')}
          nextMonthLabel={t('common:datePicker.nextMonth')}
        />
        <SearchSelect
          label={t('booking.quickRegister.genderLabel', { defaultValue: 'Gender' })}
          optional
          searchable={false}
          disabled={readOnly}
          value={form.fields.gender}
          onChange={(value) => form.setField('gender', value)}
          placeholder={t('booking.quickRegister.genderUnspecified', { defaultValue: 'Unspecified' })}
          options={[
            { value: '', label: t('booking.quickRegister.genderUnspecified', { defaultValue: 'Unspecified' }) },
            ...GENDERS.map((gender) => ({ value: gender, label: t(`booking.quickRegister.gender.${gender.toLowerCase()}`, { defaultValue: gender }) })),
          ]}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SearchSelect
          label={t('booking.quickRegister.bloodGroupLabel', { defaultValue: 'Blood group' })}
          optional
          disabled={readOnly}
          value={form.fields.bloodGroup}
          onChange={(value) => form.setField('bloodGroup', value)}
          placeholder={t('booking.quickRegister.bloodGroupUnknown', { defaultValue: 'Unknown' })}
          searchPlaceholder={t('common:actions.search')}
          noResultsLabel={t('common:list.noResults')}
          options={[
            { value: '', label: t('booking.quickRegister.bloodGroupUnknown', { defaultValue: 'Unknown' }) },
            ...BLOOD_GROUPS.map((group) => ({ value: group.value, label: group.label })),
          ]}
        />
        <Input
          label={t('booking.quickRegister.emailLabel', { defaultValue: 'Email' })}
          optional
          type="email"
          disabled={readOnly}
          value={form.fields.email}
          onChange={(event) => form.setField('email', event.target.value)}
        />
      </div>

      <Textarea
        label={t('booking.quickRegister.addressLabel', { defaultValue: 'Address' })}
        optional
        disabled={readOnly}
        rows={2}
        value={form.fields.address}
        onChange={(event) => form.setField('address', event.target.value)}
      />

      <Textarea
        label={t('booking.quickRegister.notesLabel', { defaultValue: 'Notes' })}
        optional
        disabled={readOnly}
        rows={2}
        value={form.fields.notes}
        onChange={(event) => form.setField('notes', event.target.value)}
      />

      <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
    </form>
  );
}
